import { CourseRulesEditor } from '@/components/admin/credit-settings'
import { emptyCourseRulesDraft, useCourseRulesDraft } from '@/hooks/use-course-rules-draft'
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { ImageIcon, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { TagInput } from '@/components/ui/tag-input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  createCourse,
  deleteOrDeactivateCourse,
  getCourses,
  getTrainerProfiles,
  updateCourse,
  uploadCourseImage,
} from '@/lib/api'
import { saveCourseRules } from '@/lib/credits-api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { formatCourseCredits, parseCreditAmount } from '@/lib/subscriptions'
import { cn } from '@/lib/utils'
import type { Course } from '@/types/domain'

const COURSE_STATUS_BADGE_STYLES = {
  active: 'border border-emerald-200 bg-emerald-100 text-emerald-800',
  inactive: 'border border-zinc-200 bg-zinc-100 text-zinc-700',
} as const

const courseSchema = z
  .object({
    name: z.string().trim().min(1, 'Name ist erforderlich.'),
    description: z.string(),
    level: z.literal('Einsteiger').or(z.literal('Fortgeschritten')).or(z.literal('Profi')).nullable().optional(),
    color: z.string().trim().regex(/^#[0-9A-Fa-f]{6}$/, 'Bitte eine gültige Farbe im Hex-Format wählen.'),
    duration_minutes: z.string().refine((value) => Number.isFinite(Number(value)) && Number(value) > 0, {
      message: 'Bitte eine gültige Kursdauer in Minuten eingeben.',
    }),
    credits_per_session: z.string().refine(
      (value) => parseCreditAmount(value, { allowZero: true }) !== null,
      'Bitte Credits in 0,1-Schritten ab 0 eingeben.',
    ),
    default_max_participants: z
      .string()
      .refine((value) => Number.isFinite(Number(value)) && Number(value) > 0, {
        message: 'Bitte eine gültige maximale Teilnehmerzahl eingeben.',
      }),
    default_min_participants: z
      .string()
      .refine(
        (value) => value.trim() === '' || (Number.isFinite(Number(value)) && Number(value) > 0),
        'Bitte eine gültige Mindest-Teilnehmerzahl eingeben oder leer lassen.',
      ),
    trainer_ids: z.array(z.string()),
    is_active: z.boolean(),
    image_url: z.string().nullable(),
    tags: z.array(z.string()),
    rules: z.object({
      credit_type_id: z.string().min(1, 'Bitte eine Credit-Art wählen.'),
      requires_approval: z.boolean(),
      max_guests_per_booking: z.number(),
      requires_terms: z.boolean(),
      terms_body: z.string(),
    }),
  })
  .superRefine((values, context) => {
    if (values.rules.requires_terms && values.rules.terms_body.trim() === '') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Bitte Kurs-AGB eintragen, wenn die Zustimmung vor der Buchung erforderlich ist.',
        path: ['rules', 'terms_body'],
      })
    }

    if (values.default_min_participants.trim() === '') {
      return
    }

    const min = Number(values.default_min_participants)
    const max = Number(values.default_max_participants)

    if (Number.isFinite(min) && Number.isFinite(max) && min > max) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Die Mindest-Teilnehmerzahl darf die maximale Teilnehmerzahl nicht übersteigen.',
        path: ['default_min_participants'],
      })
    }
  })

type CourseFormValues = z.infer<typeof courseSchema>

const emptyForm: CourseFormValues = {
  name: '',
  description: '',
  level: null,
  color: '#38BDF8',
  duration_minutes: '60',
  credits_per_session: '1',
  default_max_participants: '20',
  default_min_participants: '',
  trainer_ids: [],
  is_active: true,
  image_url: null,
  tags: [],
  rules: emptyCourseRulesDraft,
}

const emptyFileState = {
  file: null as File | null,
  preview: null as string | null,
}

export function AdminCoursesPage() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [editingCourse, setEditingCourse] = useState<Course | null>(null)
  const [coursePendingRemoval, setCoursePendingRemoval] = useState<Course | null>(null)
  const [imageState, setImageState] = useState(emptyFileState)
  const [statusView, setStatusView] = useState<'active' | 'inactive'>('active')
  const [searchTerm, setSearchTerm] = useState('')

  const courseForm = useForm<CourseFormValues>({
    resolver: zodResolver(courseSchema),
    defaultValues: emptyForm,
  })
  const [watchedTrainerIds, watchedRules] = useWatch({ control: courseForm.control, name: ['trainer_ids', 'rules'] })
  const editingCourseId = editingCourse?.id ?? null
  const rulesDraft = useCourseRulesDraft(editingCourseId)
  const [rulesLoadedFor, setRulesLoadedFor] = useState<string | null>(null)
  const rulesReady = editingCourseId === null || rulesLoadedFor === editingCourseId

  // Apply the saved rules once per opened course so later refetches don't overwrite edits.
  useEffect(() => {
    if (editingCourseId === null || rulesDraft === null || rulesLoadedFor === editingCourseId) return
    courseForm.setValue('rules', rulesDraft)
    // Marks the form store (an external system) as seeded for this course.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRulesLoadedFor(editingCourseId)
  }, [courseForm, editingCourseId, rulesDraft, rulesLoadedFor])

  const coursesQuery = useQuery({
    queryKey: queryKeys.courses,
    queryFn: () => getCourses(true),
  })
  const trainersQuery = useQuery({
    queryKey: queryKeys.trainerProfiles,
    queryFn: getTrainerProfiles,
  })

  const saveMutation = useMutation({
    mutationFn: async (values: CourseFormValues) => {
      const parsedDefaultMinParticipants =
        values.default_min_participants.trim() === ''
          ? null
          : Number(values.default_min_participants)

      let finalImageUrl = values.image_url
      if (imageState.file) {
        finalImageUrl = await uploadCourseImage(imageState.file)
      }

      const payload = {
        name: values.name.trim(),
        description: values.description.trim(),
        level: values.level ?? null,
        color: values.color.trim(),
        duration_minutes: Number(values.duration_minutes),
        credits_per_session: parseCreditAmount(values.credits_per_session, { allowZero: true })!,
        default_max_participants: Number(values.default_max_participants),
        default_min_participants: parsedDefaultMinParticipants,
        trainer_ids: values.trainer_ids,
        is_active: values.is_active,
        image_url: finalImageUrl,
        tags: values.tags,
      }

      let courseId = editingCourse?.id
      if (courseId) {
        await updateCourse(courseId, payload)
      } else {
        courseId = await createCourse(payload)
      }

      await saveCourseRules(courseId, values.rules)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.courses }),
        queryClient.invalidateQueries({ queryKey: ['course-rules'] }),
        queryClient.invalidateQueries({ queryKey: ['course-requirements'] }),
      ])
      setOpen(false)
      setEditingCourse(null)
      setImageState(emptyFileState)
      courseForm.reset(emptyForm)
      toast.success(editingCourse ? 'Kurs erfolgreich aktualisiert.' : 'Kurs erfolgreich erstellt.')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const removeMutation = useMutation({
    mutationFn: async (courseId: string) => deleteOrDeactivateCourse(courseId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.courses })
      setCoursePendingRemoval(null)
      toast.success(
        result === 'deleted'
          ? 'Kurs erfolgreich gelöscht.'
          : 'Kurs hat bereits Buchungen und wurde inaktiv gesetzt.',
      )
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const courses = useMemo(
    () =>
      [...(coursesQuery.data ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }),
      ),
    [coursesQuery.data],
  )
  const normalizedSearchTerm = searchTerm.trim().toLowerCase()
  const visibleCourses = useMemo(
    () =>
      courses.filter((course) => {
        if (statusView === 'active' && !course.is_active) {
          return false
        }
        if (statusView === 'inactive' && course.is_active) {
          return false
        }
        if (!normalizedSearchTerm) {
          return true
        }

        const trainerNames = course.trainers
          .map((trainer) => trainer.full_name ?? trainer.email)
          .join(' ')

        return [course.name, trainerNames]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearchTerm)
      }),
    [courses, normalizedSearchTerm, statusView],
  )
  const trainers = useMemo(() => trainersQuery.data ?? [], [trainersQuery.data])
  const existingTags = useMemo(
    () =>
      [...new Set((coursesQuery.data ?? []).flatMap((course) => course.tags))].sort((a, b) =>
        a.localeCompare(b, 'de'),
      ),
    [coursesQuery.data],
  )
  const trainerMap = useMemo(() => new Map(trainers.map((trainer) => [trainer.id, trainer])), [trainers])

  const selectedTrainerLabel = useMemo(() => {
    if (watchedTrainerIds.length === 0) {
      return 'Keine Trainer ausgewählt'
    }

    const names = watchedTrainerIds
      .map((trainerId) => {
        const trainer = trainerMap.get(trainerId)
        return trainer ? trainer.full_name ?? trainer.email : null
      })
      .filter((value): value is string => value !== null)

    if (names.length === 1) {
      return names[0]
    }

    if (names.length <= 2) {
      return names.join(', ')
    }

    return `${watchedTrainerIds.length} Trainer ausgewählt`
  }, [watchedTrainerIds, trainerMap])

  const onSubmit = courseForm.handleSubmit((values) => {
    saveMutation.mutate(values)
  })

  const creditsPerSessionField = (
    <FormField
      control={courseForm.control}
      name="credits_per_session"
      render={({ field }) => (
        <FormItem>
          <FormLabel htmlFor="course-credits-per-session">Credits pro Termin</FormLabel>
          <FormControl>
            <Input
              id="course-credits-per-session"
              type="number"
              min={0}
              step={0.1}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Kurse</h1>
          <p className="text-sm text-muted-foreground">
            Kursstammdaten inklusive Blockdauer verwalten.
          </p>
        </div>
        <Button
          onClick={() => {
            saveMutation.reset()
            setEditingCourse(null)
            setImageState(emptyFileState)
            courseForm.reset(emptyForm)
            setOpen(true)
          }}
        >
          Kurs anlegen
        </Button>
      </div>

      {coursesQuery.isError && (
        <p className="text-sm text-destructive">Kurse konnten nicht geladen werden.</p>
      )}
      {trainersQuery.isError && (
        <p className="text-sm text-destructive">{getErrorMessage(trainersQuery.error)}</p>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={statusView === 'active' ? 'default' : 'outline'}
            onClick={() => setStatusView('active')}
          >
            Aktive Kurse
          </Button>
          <Button
            type="button"
            size="sm"
            variant={statusView === 'inactive' ? 'default' : 'outline'}
            onClick={() => setStatusView('inactive')}
          >
            Inaktive Kurse
          </Button>
        </div>
        <Input
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Kurs, Level oder Trainer suchen ..."
          className="w-full sm:max-w-xs"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {visibleCourses.map((course) => {
          const trainerNames = course.trainers
            .map((trainer) => trainer.full_name ?? trainer.email)
            .sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base' }))

          return (
            <Card key={course.id} className="h-full">
              <CardHeader className="flex flex-row items-start justify-between space-y-0 p-4 pb-2">
                <div>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <span
                      className="h-2.5 w-2.5 rounded-full border border-border"
                      style={{ backgroundColor: course.color }}
                      aria-hidden
                    />
                    <span>{course.name}</span>
                  </CardTitle>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        course.is_active ? COURSE_STATUS_BADGE_STYLES.active : COURSE_STATUS_BADGE_STYLES.inactive
                      }`}
                    >
                      {course.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      saveMutation.reset()
                      setEditingCourse(course)
                      courseForm.reset({
                        name: course.name,
                        description: course.description ?? '',
                        level: course.level,
                        color: course.color,
                        duration_minutes: String(course.duration_minutes),
                        credits_per_session: String(course.credits_per_session),
                        default_max_participants: String(course.default_max_participants),
                        default_min_participants:
                          course.default_min_participants == null
                            ? ''
                            : String(course.default_min_participants),
                        trainer_ids: course.trainers.map((trainer) => trainer.id),
                        is_active: course.is_active,
                        image_url: course.image_url,
                        tags: course.tags,
                        rules: emptyCourseRulesDraft,
                      })
                      setRulesLoadedFor(null)
                      setImageState({ file: null, preview: course.image_url })
                      setOpen(true)
                    }}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    aria-label={`${course.name} löschen oder deaktivieren`}
                    onClick={() => {
                      removeMutation.reset()
                      setCoursePendingRemoval(course)
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 p-4 pt-0">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div className="rounded-md border border-border/70 bg-muted/40 p-2">
                    <p className="text-[11px] text-muted-foreground">Dauer</p>
                    <p className="text-sm font-medium">{course.duration_minutes} Min</p>
                  </div>
                  <div className="rounded-md border border-border/70 bg-muted/40 p-2">
                    <p className="text-[11px] text-muted-foreground">Credits</p>
                    <p className="text-sm font-medium">{formatCourseCredits(course.credits_per_session)}</p>
                  </div>
                  <div className="rounded-md border border-border/70 bg-muted/40 p-2">
                    <p className="text-[11px] text-muted-foreground">Mind. Plätze</p>
                    <p className="text-sm font-medium">{course.default_min_participants ?? '-'}</p>
                  </div>
                  <div className="rounded-md border border-border/70 bg-muted/40 p-2">
                    <p className="text-[11px] text-muted-foreground">Max. Plätze</p>
                    <p className="text-sm font-medium">{course.default_max_participants}</p>
                  </div>
                </div>
                <div className="rounded-md border border-border/70 bg-muted/25 p-2">
                  <p className="text-[11px] text-muted-foreground">Trainer</p>
                  <p className="text-sm font-medium leading-snug">
                    {trainerNames.length > 0 ? trainerNames.join(', ') : 'Keine Trainer'}
                  </p>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>
      {visibleCourses.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Keine {statusView === 'active' ? 'aktiven' : 'inaktiven'} Kurse gefunden.
        </p>
      )}

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen)
          if (!nextOpen) {
            setEditingCourse(null)
            setImageState(emptyFileState)
            saveMutation.reset()
            courseForm.reset(emptyForm)
          }
        }}
      >
        <DialogContent className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCourse ? 'Kurs bearbeiten' : 'Kurs erstellen'}</DialogTitle>
            <DialogDescription>
              Lege Name, Farbe, Beschreibung, Dauer und optional zugeordnete Trainer fest.
            </DialogDescription>
          </DialogHeader>

          <Form {...courseForm}>
            <form onSubmit={onSubmit} className="grid gap-4 py-2">
              <FormField
                control={courseForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="course-name">Name</FormLabel>
                    <FormControl>
                      <Input id="course-name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <Label>Titelbild</Label>
                <div className="flex flex-col gap-3">
                  {imageState.preview ? (
                    <div className="relative aspect-video w-full overflow-hidden rounded-md border border-border">
                      <img
                        src={imageState.preview}
                        alt="Vorschau"
                        className="h-full w-full object-cover"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute right-2 top-2 h-7 w-7"
                        onClick={() => {
                          setImageState(emptyFileState)
                          courseForm.setValue('image_url', null, { shouldDirty: true })
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <label
                      htmlFor="course-image"
                      className="flex aspect-video w-full cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-border bg-muted/30 hover:bg-muted/50"
                    >
                      <ImageIcon className="mb-2 h-8 w-8 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Bild auswählen</span>
                      <input
                        id="course-image"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(event) => {
                          const file = event.target.files?.[0]
                          if (!file) {
                            return
                          }

                          const reader = new FileReader()
                          reader.onloadend = () => {
                            setImageState({
                              file,
                              preview: reader.result as string,
                            })
                          }
                          reader.readAsDataURL(file)
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>


              <FormField
                control={courseForm.control}
                name="color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="course-color">Farbe</FormLabel>
                    <div className="flex items-center gap-3">
                      <Input
                        id="course-color"
                        type="color"
                        className="h-10 w-16 cursor-pointer p-1"
                        value={/^#[0-9A-Fa-f]{6}$/.test(field.value) ? field.value : '#38BDF8'}
                        onChange={field.onChange}
                      />
                      <FormControl>
                        <Input value={field.value} maxLength={7} onChange={field.onChange} />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={courseForm.control}
                name="duration_minutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="course-duration">Dauer (Minuten)</FormLabel>
                    <FormControl>
                      <Input id="course-duration" type="number" min={5} step={5} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={courseForm.control}
                name="default_min_participants"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="course-default-min-participants">
                      Mindest-Teilnehmerzahl (optional)
                    </FormLabel>
                    <FormControl>
                      <Input
                        id="course-default-min-participants"
                        type="number"
                        min={1}
                        step={1}
                        placeholder="Optional"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={courseForm.control}
                name="default_max_participants"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="course-default-max-participants">Maximale Teilnehmerzahl</FormLabel>
                    <FormControl>
                      <Input
                        id="course-default-max-participants"
                        type="number"
                        min={1}
                        step={1}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={courseForm.control}
                name="trainer_ids"
                render={({ field, fieldState }) => (
                  <FormItem>
                    <FormLabel>Trainer (0 bis n)</FormLabel>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            'w-full justify-start font-normal',
                            fieldState.invalid && 'border-destructive text-destructive',
                          )}
                        >
                          {selectedTrainerLabel}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="w-[var(--radix-dropdown-menu-trigger-width)]"
                      >
                        {trainers.map((trainer) => (
                          <DropdownMenuCheckboxItem
                            key={trainer.id}
                            checked={(field.value ?? []).includes(trainer.id)}
                            onSelect={(event) => event.preventDefault()}
                            onCheckedChange={(checked) => {
                              const currentIds = field.value ?? []
                              const nextIds =
                                checked === true
                                  ? Array.from(new Set([...currentIds, trainer.id]))
                                  : currentIds.filter((trainerId) => trainerId !== trainer.id)
                              field.onChange(nextIds)
                            }}
                          >
                            {trainer.full_name ?? trainer.email}
                          </DropdownMenuCheckboxItem>
                        ))}
                        {trainers.length === 0 && (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">
                            Keine aktiven Trainer vorhanden.
                          </div>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={courseForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="course-description">Beschreibung</FormLabel>
                    <FormControl>
                      <Textarea id="course-description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={courseForm.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="course-tags">Tags</FormLabel>
                    <FormControl>
                      <TagInput
                        id="course-tags"
                        placeholder="Tag eingeben und mit Enter bestätigen"
                        suggestions={existingTags}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Mitglieder filtern den Kalender nach diesen Tags.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={courseForm.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={field.value ? 'active' : 'inactive'}
                      onValueChange={(value) => field.onChange(value === 'active')}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Aktiv</SelectItem>
                        <SelectItem value="inactive">Inaktiv</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {rulesReady
                ? (
                  <CourseRulesEditor
                    value={watchedRules}
                    onChange={(rules) => courseForm.setValue('rules', rules, { shouldValidate: courseForm.formState.isSubmitted })}
                    termsError={courseForm.formState.errors.rules?.terms_body?.message}
                  >
                    {creditsPerSessionField}
                  </CourseRulesEditor>
                )
                : <p className="text-sm text-muted-foreground">Kursregeln werden geladen …</p>}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={saveMutation.isPending || !rulesReady}>
                  {saveMutation.isPending ? 'Speichere ...' : 'Speichern'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={coursePendingRemoval !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !removeMutation.isPending) {
            setCoursePendingRemoval(null)
            removeMutation.reset()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kurs löschen oder deaktivieren?</DialogTitle>
            <DialogDescription>
              Wenn der Kurs keine Termine mit Buchungen hat, wird er endgültig gelöscht. Sobald
              mindestens eine Buchung existiert, bleibt der Verlauf erhalten und der Kurs wird
              stattdessen inaktiv gesetzt.
            </DialogDescription>
          </DialogHeader>

          {coursePendingRemoval && (
            <div className="rounded-md border border-border/70 bg-muted/30 p-3 text-sm">
              <p className="font-medium">{coursePendingRemoval.name}</p>
              <p className="text-muted-foreground">
                Inaktive Kurse verschwinden aus der öffentlichen Kursauswahl, bleiben aber für
                bestehende Historie erhalten.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={removeMutation.isPending}
              onClick={() => {
                setCoursePendingRemoval(null)
                removeMutation.reset()
              }}
            >
              Abbrechen
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={removeMutation.isPending || !coursePendingRemoval}
              onClick={() => {
                if (coursePendingRemoval) {
                  removeMutation.mutate(coursePendingRemoval.id)
                }
              }}
            >
              {removeMutation.isPending ? 'Prüfe ...' : 'Fortfahren'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
